import type { DailyReturnPoint } from '@/components/ReportDashboard/Overview/DailyReturnChart'
import type { AttributionGfpaSummaryDatum } from '@/services/report-dashboard/raDashboard.data.service'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export const DAILY_RETURN_TARGET_TICKS = 12

// ~targetTicks evenly spaced category tick indices across [startIndex, endIndex] (constant step).
export function dailyReturnTickIndices(
  startIndex: number,
  endIndex: number,
  targetTicks = DAILY_RETURN_TARGET_TICKS,
): number[] {
  if (endIndex < startIndex) return []
  const step = Math.max(1, Math.ceil((endIndex - startIndex + 1) / targetTicks))
  const ticks: number[] = []
  for (let i = startIndex; i <= endIndex; i += step) ticks.push(i)
  return ticks
}

// Map the cached GFPA daily summaries to the Daily Return chart's point shape, sorted by date.
// Dates are formatted in UTC — the summary dates come back as UTC midnight, so formatting in
// local time would shift the label back a day for anyone west of UTC.
export function mapDailyReturns(
  summaries: Record<string, AttributionGfpaSummaryDatum> | null | undefined,
): DailyReturnPoint[] | undefined {
  if (!summaries) return undefined
  return Object.values(summaries)
    .slice()
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((d) => ({
      date: dayjs.utc(d.date).format('MMM D, YYYY'),
      portfolio: d.portfolioReturn,
      benchmark: d.benchmarkReturn,
      active: d.activeReturn,
    }))
}

export interface DailyReturnRange {
  beginDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
}

// Map the daily summaries to chart points, keeping only those within the given range. Filtering is done
// on the raw summary Date (not the formatted label), so the display format can change without emptying
// the chart.
export function clampSummariesToRange(
  summaries: Record<string, AttributionGfpaSummaryDatum> | null | undefined,
  range?: DailyReturnRange | null,
): DailyReturnPoint[] {
  const points = mapDailyReturnsWithTimestamp(summaries)
  const inRange = range
    ? points.filter(
        (p) =>
          p.timestamp >= range.beginDate.startOf('day').valueOf() &&
          p.timestamp <= range.endDate.endOf('day').valueOf(),
      )
    : points
  return inRange.map(({ timestamp: _timestamp, ...point }) => point)
}

export function mapDailyReturnsWithTimestamp(
  summaries: Record<string, AttributionGfpaSummaryDatum> | null | undefined,
): (DailyReturnPoint & { timestamp: number })[] {
  if (!summaries) return []
  return Object.values(summaries)
    .slice()
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((d) => ({
      timestamp: d.date.getTime(),
      date: dayjs.utc(d.date).format('MMM D, YYYY'),
      portfolio: d.portfolioReturn,
      benchmark: d.benchmarkReturn,
      active: d.activeReturn,
    }))
}
